/**
 * BoothOSPage — /booth-os/:event_id
 *
 * Orchestration only. After the 2026-05-14 P1 refactor, every
 * capture surface lives in its own module under
 * `/app/frontend/src/components/booth-os/`. This file's job is
 * pure routing between views + lifecycle wiring.
 *
 * View vocabulary (unchanged):
 *   'launcher' | 'memory_snap' | 'photo_strip' | 'video_message'
 *   | 'flip_guestbook' | 'qr_continuity' | 'theme_picker' | 'attract'
 *
 * Decisions locked by Craig:
 *   • Reuse existing capture engines — NEVER rebuild.
 *   • After every completed flow: QR continuity with the gallery
 *     code. The booth is a doorway, not a destination.
 *   • Atmosphere (5 themes) persists across nights via
 *     sessionStorage; pairing memory is the only thing the
 *     retune-room gesture clears.
 *
 * Hard bans:
 *   • No cyan/neon, no arcade UI, no gamified motion, no glow.
 *   • No "Success!" / "Share Now!" / "Upload Complete!" — copy is
 *     preservation, not engagement.
 *   • No SaaS chrome / dashboard nav — kiosk only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import BoothAtmosphereProvider from '../components/booth-os/BoothAtmosphereProvider';
import BoothLauncher from '../components/booth-os/BoothLauncher';
import BoothMemorySnap from '../components/booth-os/BoothMemorySnap';
import PhotoStripCapture from '../components/booth-os/PhotoStripCapture';
import VideoMessageCapture from '../components/booth-os/VideoMessageCapture';
import FlipGuestbookCapture from '../components/booth-os/FlipGuestbookCapture';
import QRContinuity from '../components/booth-os/QRContinuity';
import IdleAttract from '../components/booth-os/IdleAttract';
import ThemePicker from '../components/booth-os/ThemePicker';
import RetuneRoomButton from '../components/booth-os/RetuneRoomButton';
import CompositionGuideToggle from '../components/booth-os/CompositionGuideToggle';
import {
  Eyebrow, Heading, Lede, CenteredCopy,
} from '../components/booth-os/boothShellAtoms';

import { useBoothTheme } from '../hooks/useBoothTheme';
import { useBoothIdleTimer } from '../hooks/useBoothIdleTimer';
import {
  subscribe as subscribeQueue,
  kickDrain,
} from '../lib/booth-os/uploadQueue';
import { saveGuestSession, loadGuestSession } from '../shared/guestSession';
import { useResilientEvent } from '../hooks/useResilientEvent';
import { useHistoryView } from '../shared/navigation/useHistoryView';
import { isLeaversEdition } from '../shared/packageGuestProducts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoothOSPage = () => {
  const { event_id } = useParams();
  const navigate = useNavigate();
  const { themeId, setThemeId, theme } = useBoothTheme();

  // iter194 — Booth OS uses the shared useResilientEvent hook.
  // sessionStorage cache hydrates instantly on mount; background
  // refresh on every nav. Only ever blanks the launcher when there
  // is genuinely no cached event to render.
  const { event, error } = useResilientEvent({
    cacheKey: `booth_os_event_${event_id}`,
    enabled: !!event_id,
    fetcher: () => axios.get(`${API}/events/${event_id}/booth-info`).then((r) => r.data),
    validate: (e) => e && e.event_id === event_id,
    onSuccess: (fresh) => {
      try {
        localStorage.setItem('booth_os_paired_event_id', fresh.event_id);
        localStorage.setItem('booth_os_paired_event_name', fresh.event_name || '');
      } catch {}
      if (fresh?.slug) {
        const existing = loadGuestSession(fresh.slug);
        saveGuestSession({
          slug: fresh.slug,
          guest_id: existing?.guest_id || `booth_${fresh.event_id}`,
          guest_name: existing?.guest_name || '',
          entry: 'booth_os',
          event_id: fresh.event_id,
        });
      }
    },
  });

  // iter197 — Leavers Night never enters through Booth OS.
  // Booth OS is a wedding-tier kiosk surface (Room / Archive); a
  // Leavers Night event reaching /booth-os/:eventId is a pathway
  // bug, not a legitimate use. Redirect to /access/:slug — the
  // canonical schools entrance (zero-friction, 8-card matrix).
  //
  // This belt-and-braces guard handles deep links, stale Pro
  // dashboard buttons, paired booth tablets carrying a Leavers
  // event by mistake, etc.
  useEffect(() => {
    if (event && isLeaversEdition(event) && event.slug) {
      navigate(`/access/${event.slug}`, { replace: true });
    }
  }, [event, navigate]);

  // 'launcher' | 'memory_snap' | 'photo_strip' | 'video_message'
  // | 'flip_guestbook' | 'qr_continuity' | 'theme_picker' | 'attract'
  const [view, setView] = useState('launcher');
  // iter197 — Universal Back Navigation Contract. Sync the in-app
  // view state with browser history so pressing the device back
  // button INSIDE Booth OS returns to the launcher view instead of
  // exiting Booth OS entirely. "Back should feel like moving
  // through memory, not navigating software."
  //
  // iter213 ✦ — `attract` is a transient idle overlay, not a
  // navigable screen. Production rehearsal observed that syncing
  // it with history caused the wake-tap to fire `history.back()`,
  // which on fullscreen kiosks could ping-pong popstate cascades
  // (or restore stale tagged views), leaving the user "glitched"
  // on the screensaver. Pass `attract` as transient so wake-tap
  // is a pure state flip.
  useHistoryView({
    view,
    setView,
    rootView: 'launcher',
    tag: `booth_os_${event_id || 'unknown'}`,
    transientViews: ['attract'],
  });
  // What flow just finished — drives the QR continuity copy.
  const [lastFlow, setLastFlow] = useState(null);
  // True iff the just-completed flow couldn't reach the server yet
  // (queued locally in IDB). Drives the QR continuity language swap
  // from "Your memory has been preserved ✦" → "Preserved locally ✦".
  const [pendingDeferred, setPendingDeferred] = useState(false);

  // Idle attract timer — pauses on every view except 'launcher'.
  const onIdle = useCallback(() => setView('attract'), []);
  const { bumpNow } = useBoothIdleTimer(view, onIdle);

  // Boot the queue drain on mount + subscribe to pending-count
  // changes. If a queued item lands while the QR continuity is up,
  // the copy quietly flips from "Preserved locally ✦" back to
  // "Your memory has been preserved ✦" — no banner, no toast.
  useEffect(() => {
    kickDrain();
    return subscribeQueue(({ pending }) => {
      if (pending === 0) setPendingDeferred(false);
    });
  }, []);

  const onCardPress = (id) => {
    if (!event) return;
    bumpNow();
    if (id === 'photo')        setView('photo_strip');
    else if (id === 'video')   setView('video_message');
    else if (id === 'guestbook') setView('flip_guestbook');
    else if (id === 'memory_snap') setView('memory_snap');
  };

  const onFlowComplete = (flowId, opts = {}) => {
    setLastFlow(typeof flowId === 'string' ? flowId : null);
    setPendingDeferred(!!opts.deferred);
    bumpNow();
    // iter210 ✦ — Personal Memory Return Path.
    // If the capture surface returned a contribution_id we navigate
    // directly to the Personal Memory Page (with the kiosk param so
    // the "Pass to the next memory ✦" gesture renders for the
    // shared-device flow). Surfaces that haven't been wired to
    // expose contribution_id yet fall back to the legacy
    // QRContinuity screen — preserves the old behaviour until each
    // capture flow is updated. Once all four pass contribution_id,
    // the qr_continuity branch becomes dead code and can be removed.
    const contributionId = opts.contribution_id || opts.contributionId;
    if (contributionId && event?.slug) {
      navigate(`/event/${event.slug}/m/${contributionId}?kiosk=1`);
      return;
    }
    setView('qr_continuity');
  };

  // ── render ────────────────────────────────────────────────────
  if (error) {
    return (
      <BoothAtmosphereProvider theme={theme}>
        <CenteredCopy theme={theme}>
          <Eyebrow theme={theme}>✦ BOOTH CLOSED</Eyebrow>
          <Heading theme={theme}>The booth couldn’t open this event.</Heading>
          <Lede theme={theme}>{error}</Lede>
        </CenteredCopy>
      </BoothAtmosphereProvider>
    );
  }
  if (!event) {
    return (
      <BoothAtmosphereProvider theme={theme}>
        <CenteredCopy theme={theme}>
          <Lede theme={theme}>Opening the booth…</Lede>
        </CenteredCopy>
      </BoothAtmosphereProvider>
    );
  }

  return (
    <BoothAtmosphereProvider theme={theme} fullscreenable>
      {view === 'launcher' && (
        <BoothLauncher
          theme={theme}
          event={event}
          onCardPress={onCardPress}
          onOpenThemePicker={() => setView('theme_picker')}
        />
      )}
      {view === 'memory_snap' && (
        <BoothMemorySnap
          theme={theme}
          event={event}
          onBack={() => setView('launcher')}
          onComplete={(opts) => onFlowComplete('memory_snap', opts)}
        />
      )}
      {view === 'photo_strip' && (
        <PhotoStripCapture
          theme={theme}
          event={event}
          onBack={() => setView('launcher')}
          onComplete={(opts) => onFlowComplete('photo_strip', opts)}
        />
      )}
      {view === 'video_message' && (
        <VideoMessageCapture
          theme={theme}
          event={event}
          onBack={() => setView('launcher')}
          onComplete={(opts) => onFlowComplete('video_message', opts)}
        />
      )}
      {view === 'flip_guestbook' && (
        <FlipGuestbookCapture
          theme={theme}
          event={event}
          onBack={() => setView('launcher')}
          onComplete={(opts) => onFlowComplete('flip_guestbook', opts)}
        />
      )}
      {view === 'qr_continuity' && (
        <QRContinuity
          theme={theme}
          event={event}
          flowId={lastFlow}
          deferred={pendingDeferred}
          onReturn={() => setView('launcher')}
        />
      )}
      {view === 'attract' && (
        <IdleAttract
          theme={theme}
          event={event}
          onWake={() => { bumpNow(); setView('launcher'); }}
        />
      )}
      {/* "Choose another night ✦" — operator retune gesture.
          Rendered ONLY on the launcher AND the QR continuity screen.
          Never on any active capture surface. Same hidden long-press
          family, same 1.5s hold, same dissolve, same copy. Operators
          feel empowered; guests feel nothing changed. */}
      {(view === 'launcher' || view === 'qr_continuity') && (
        <RetuneRoomButton theme={theme} />
      )}
      {/* iter199 — "Composition guide ✦" operator long-press lives
          in the bottom-LEFT corner, mirroring the retune gesture
          in mechanic but driving the FramingGuide overlay. Same
          render conditions: launcher + QR continuity only. Guests
          never see it. */}
      {(view === 'launcher' || view === 'qr_continuity') && (
        <CompositionGuideToggle theme={theme} />
      )}
      {view === 'theme_picker' && (
        <ThemePicker
          theme={theme}
          activeId={themeId}
          onPick={(id) => { setThemeId(id); setView('launcher'); }}
          onClose={() => setView('launcher')}
        />
      )}
    </BoothAtmosphereProvider>
  );
};

export default BoothOSPage;
